import { announce, cartRequest, cartSections, fetchSection, openDialog, parseHTML, renderCartSections, routes } from 'theme';

/**
 * The product page and the featured product section. When the customer picks an option, Shopify renders the section again
 * for the matching variant, using the option value IDs, so prices, availability, buttons and pickup details always come
 * from Shopify. The blocks marked data-variant-region are swapped in, the address bar shows the variant, and the variant's
 * media moves to the front of the gallery. Picking a value that belongs to another product of a combined listing loads
 * that product instead.
 */
/**
 * The address bar shows the selected variant on the product page, so a shared link opens the same choice. Only an element
 * marked data-update-url does this, so a featured product elsewhere leaves the address alone.
 *
 * @param {HTMLElement} element
 */
function syncAddress(element) {
  if (!('updateUrl' in element.dataset)) return;
  const address = new URL(element.dataset.productUrl, window.location.href);
  if (element.dataset.variantId) address.searchParams.set('variant', element.dataset.variantId);
  window.history.replaceState({}, '', address);
}

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class extends HTMLElement {
      /** @type {AbortController|undefined} */
      #request;

      #onChange = (event) => {
        const field = event.target;
        if (!(field instanceof Element) || !field.closest('variant-picker')) return;
        const picked = field instanceof HTMLSelectElement ? field.selectedOptions[0] : field;
        const valueIds = [...this.querySelectorAll('variant-picker input:checked, variant-picker select')].map((input) =>
          input instanceof HTMLSelectElement ? input.selectedOptions[0]?.dataset.optionValueId : input.dataset.optionValueId,
        );
        const productUrl = picked?.dataset.productUrl || this.dataset.productUrl;
        this.#update(productUrl, valueIds.filter(Boolean), field.id);
      };

      async #update(productUrl, valueIds, focusId) {
        this.#request?.abort();
        this.#request = new AbortController();
        const url = `${productUrl}?option_values=${valueIds.join(',')}`;

        try {
          const source = await fetchSection(url, this.dataset.sectionId, { signal: this.#request.signal });
          const fresh = source.getElementById(this.id);
          if (!fresh) return;

          if (productUrl !== this.dataset.productUrl) {
            // A combined listing: the option belongs to another product, so the whole product is replaced.
            this.replaceWith(fresh);
            syncAddress(fresh);
            document.getElementById(focusId)?.focus();
            return;
          }

          for (const region of this.querySelectorAll('[data-variant-region]')) {
            const next = fresh.querySelector(`#${CSS.escape(region.id)}`);
            if (next) region.replaceChildren(...next.childNodes);
          }

          this.dataset.variantId = fresh.dataset.variantId || '';
          syncAddress(this);

          const mediaId = fresh.dataset.featuredMediaId;
          if (mediaId && mediaId !== this.dataset.featuredMediaId) {
            this.dataset.featuredMediaId = mediaId;
            this.querySelector('product-gallery')?.showMedia(mediaId);
          }

          document.getElementById(focusId)?.focus();
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        }
      }

      connectedCallback() {
        this.addEventListener('change', this.#onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.#onChange);
        this.#request?.abort();
      }
    },
  );
}

/**
 * The media gallery. On small screens it scrolls sideways, with a counter and previous and next buttons. showMedia moves a
 * variant's media to the front and brings it into view.
 */
if (!customElements.get('product-gallery')) {
  customElements.define(
    'product-gallery',
    class extends HTMLElement {
      /** @type {IntersectionObserver|undefined} */
      #observer;

      connectedCallback() {
        const list = this.querySelector('.gallery__list');
        const counter = this.querySelector('[data-gallery-current]');
        if (!list) return;

        this.addEventListener('click', this.#onClick);
        if (counter) {
          this.#observer = new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                if (entry.isIntersecting) counter.textContent = String([...list.children].indexOf(entry.target) + 1);
              }
            },
            { root: list, threshold: 0.6 },
          );
          for (const item of list.children) this.#observer.observe(item);
        }
      }

      disconnectedCallback() {
        this.#observer?.disconnect();
        this.removeEventListener('click', this.#onClick);
      }

      #onClick = (event) => {
        const button = event.target.closest('button[data-step]');
        if (!button) return;
        const list = this.querySelector('.gallery__list');
        list.scrollBy({ left: Number(button.dataset.step) * list.clientWidth, behavior: 'smooth' });
      };

      /** @param {string} mediaId */
      showMedia(mediaId) {
        const list = this.querySelector('.gallery__list');
        const item = list?.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
        if (!item) return;
        list.prepend(item);
        list.scrollTo({ left: 0, behavior: 'smooth' });
        if (list.getBoundingClientRect().top < 0) list.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
  );
}

/**
 * Adds to cart without leaving the page when the cart type is Drawer, then opens the drawer. Shopify's own message shows when
 * it can't add the item, such as when it's sold out. With the cart page instead, the form submits as usual.
 */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class extends HTMLElement {
      #onSubmit = async (event) => {
        const drawer = document.getElementById('CartDrawer');
        if (!(drawer instanceof HTMLDialogElement)) return;
        event.preventDefault();

        const form = /** @type {HTMLFormElement} */ (event.target);
        const button = form.querySelector('button[name="add"]');
        const error = this.querySelector('[data-product-error]');
        if (!button || button.getAttribute('aria-busy') === 'true') return;

        button.setAttribute('aria-busy', 'true');
        if (error) error.hidden = true;

        const body = new FormData(form);
        body.append('sections', cartSections().join(','));
        body.append('sections_url', window.location.pathname);

        try {
          const response = await cartRequest(routes.cartAdd, body);
          renderCartSections(response.sections);
          openDialog(drawer, button);
          announce(this.dataset.addedMessage);
        } catch (failure) {
          if (error) {
            error.textContent = failure.message;
            error.hidden = false;
          }
        } finally {
          button.removeAttribute('aria-busy');
        }
      };

      connectedCallback() {
        this.addEventListener('submit', this.#onSubmit);
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.#onSubmit);
      }
    },
  );
}

/**
 * Videos from YouTube or Vimeo, and 3D models, load only when the customer selects them. Models then get Shopify's model
 * viewer controls.
 */
if (!customElements.get('deferred-media')) {
  customElements.define(
    'deferred-media',
    class extends HTMLElement {
      #onClick = (event) => {
        if (!event.target.closest('[data-load-media]')) return;
        const template = this.querySelector('template');
        if (!template) return;
        const content = template.content.cloneNode(true);
        for (const part of this.querySelectorAll('[data-load-media]')) part.remove();
        this.append(content);
        template.remove();

        const model = this.querySelector('model-viewer');
        if (model) {
          window.Shopify?.loadFeatures?.([
            {
              name: 'model-viewer-ui',
              version: '1.0',
              onLoad: (errors) => {
                if (!errors && window.Shopify?.ModelViewerUI) new window.Shopify.ModelViewerUI(model);
              },
            },
          ]);
        }
        this.querySelector('iframe, video, model-viewer')?.focus();
      };

      connectedCallback() {
        this.addEventListener('click', this.#onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
      }
    },
  );
}

// View in your space: Shopify's AR library reads the product's 3D models and sets up their buttons.
const modelData = document.querySelector('script[data-shopify-models]');
if (modelData) {
  const setUpXR = (errors) => {
    if (errors) return;
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => setUpXR(), { once: true });
      return;
    }
    window.ShopifyXR.addModels(JSON.parse(modelData.textContent));
    window.ShopifyXR.setupXRElements();
  };
  window.Shopify?.loadFeatures?.([{ name: 'shopify-xr', version: '1.0', onLoad: setUpXR }]);
}

/**
 * Pickup availability for the selected variant. When the variant changes, the block is replaced and a new element loads the
 * new variant's availability.
 */
if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class extends HTMLElement {
      async connectedCallback() {
        const root = routes.root.replace(/\/$/, '');
        try {
          const response = await fetch(`${root}/variants/${this.dataset.variantId}/?section_id=pickup-availability`);
          if (!response.ok) return;
          const section = parseHTML(await response.text()).querySelector('.shopify-section');
          if (!section || !this.isConnected) return;
          // Each pickup dialog gets an ID of its own, so two products on one page can't share one.
          for (const element of section.querySelectorAll('[id]')) element.id = `${this.id}-${element.id}`;
          for (const element of section.querySelectorAll('[aria-labelledby]')) {
            element.setAttribute('aria-labelledby', `${this.id}-${element.getAttribute('aria-labelledby')}`);
          }
          for (const element of section.querySelectorAll('[data-dialog-open]')) {
            element.setAttribute('data-dialog-open', `${this.id}-${element.getAttribute('data-dialog-open')}`);
          }
          this.replaceChildren(...section.childNodes);
        } catch {
          // Pickup details are extra information, so the page carries on without them.
        }
      }
    },
  );
}

/**
 * Related and complementary products. Shopify fills them in when they're requested, so they load as the customer scrolls
 * near them, and stay empty when Shopify has nothing to recommend.
 */
if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class extends HTMLElement {
      /** @type {IntersectionObserver|undefined} */
      #observer;

      connectedCallback() {
        if (this.childElementCount > 0 || !this.dataset.url) return;
        this.#observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.#observer.disconnect();
            this.#load();
          },
          { rootMargin: '0px 0px 400px 0px' },
        );
        this.#observer.observe(this);
      }

      disconnectedCallback() {
        this.#observer?.disconnect();
      }

      async #load() {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) return;
          const fresh = parseHTML(await response.text()).getElementById(this.id);
          if (fresh?.childElementCount) this.replaceChildren(...fresh.childNodes);
        } catch {
          // Recommendations are optional, so nothing shows if they can't load.
        }
      }
    },
  );
}

/**
 * The gift card recipient form. Its fields show, and are sent, only when the customer chooses to send the card to someone.
 * Shopify uses the customer's time zone offset to send the card on the chosen date.
 */
if (!customElements.get('gift-card-recipient')) {
  customElements.define(
    'gift-card-recipient',
    class extends HTMLElement {
      #onChange = (event) => {
        if (!event.target.matches('[data-recipient-toggle]')) return;
        this.#toggle(event.target.checked);
      };

      #toggle(open) {
        const fields = this.querySelector('[data-recipient-fields]');
        if (!fields) return;
        fields.hidden = !open;
        fields.disabled = !open;
      }

      connectedCallback() {
        const offset = this.querySelector('[data-recipient-offset]');
        if (offset) offset.value = String(new Date().getTimezoneOffset());
        this.addEventListener('change', this.#onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.#onChange);
      }
    },
  );
}
